import { useForm } from "@tanstack/react-form";
import { useEffect } from "react";
import { Button } from "../../../components/Button";
import { Form } from "../../../components/Form";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { FormActions } from "../../../components/FormActions";
import { Input } from "../../../components/Input";
import styled from "styled-components";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { useGetStoreQuery } from "../../../query/storesQuery";
import { IntegrationType } from "@KassaroAB/shared/types/enums/IntegrationType";
import { useTranslation } from "react-i18next";
import type {
  RestIntegration,
} from "@KassaroAB/shared/types/integrations/integration";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import {
  useCreateIntegrationMutation,
  useUpdateIntegrationMutation,
} from "../../../query/integrationsQuery";

const ErrorMessage = styled.div`
  color: ${colors.danger};
  font-size: ${fontSizes.small}px;
  margin-top: ${spacing.x05}px;
`;

type RestIntegrationNets = Extract<RestIntegration, { type: IntegrationType.NETS }>;

interface NetsFormProps {
  storeId: StoreId;
  integration?: RestIntegrationNets;
  onSuccess: () => void;
  onError: () => void;
  onCancel: () => void;
}

export const NetsForm = ({
  storeId,
  integration,
  onSuccess,
  onError,
  onCancel,
}: NetsFormProps) => {
  const { t } = useTranslation();
  const { data: store } = useGetStoreQuery(storeId);
  const { mutate: createMutation } = useCreateIntegrationMutation(
    store?.chainId ?? null,
    storeId,
  );
  const { mutate: updateMutation } = useUpdateIntegrationMutation(
    store?.chainId ?? null,
  );

  const form = useForm({
    defaultValues: {
      name:
        integration?.name ||
        (store ? t("integrationDetails.netsDefaultName", { storeName: store.name }) : "Nets"),
      username: integration?.username || "",
      password: "",
    },
    onSubmit: ({ value }) => {
      if (integration) {
        const updatePayload = {
          type: IntegrationType.NETS as const,
          name: value.name,
          username: value.username,
          ...(value.password && { password: value.password }),
        };
        updateMutation(
          { integrationId: integration.id, integration: updatePayload },
          { onSuccess: () => onSuccess(), onError: () => onError() },
        );
      } else {
        const createPayload = {
          type: IntegrationType.NETS as const,
          name: value.name,
          username: value.username,
          password: value.password,
        };
        createMutation(createPayload, {
          onSuccess: () => onSuccess(),
          onError: () => onError(),
        });
      }
    },
  });

  useEffect(() => {
    if (store && !integration) {
      form.setFieldValue(
        "name",
        t("integrationDetails.netsDefaultName", { storeName: store.name }),
      );
    }
  }, [store, integration, form, t]);

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <form.Field
        name="name"
        validators={{
          onChange: ({ value }) => (!value ? t("integrationDetails.nameRequired") : undefined),
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("integrationDetails.nameLabel")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("integrationDetails.namePlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="username"
        validators={{
          onChange: ({ value }) => (!value ? t("integrationDetails.usernameRequired") : undefined),
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("integrationDetails.usernameLabel")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("integrationDetails.usernamePlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="password"
        validators={{
          onChange: ({ value }) => {
            if (integration) return undefined;
            return !value ? t("integrationDetails.passwordRequired") : undefined;
          },
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>
              {integration
                ? t("integrationDetails.passwordLabel")
                : t("integrationDetails.passwordLabelNew")}
            </FormLabel>
            <Input
              type="password"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required={!integration}
              placeholder={t("integrationDetails.passwordPlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <FormActions>
        <Button type="button" $color="secondary" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button type="submit">{integration ? t("common.update") : t("common.create")}</Button>
      </FormActions>
    </Form>
  );
};
